const EXIT_MODAL_DO_NOT_SHOW_SESSION_STORAGE_KEY = 'exit-modal-do-not-show';
const EXIT_MODAL_SHOW_ONCE_COOKIE_KEY = 'EXIT_MODAL_ALREADY_SHOWN';
const EXIT_MODAL_SHOW_ONCE_COOKIE_TTL = 300000; // 5 minutes; an arbitrary short TTL.
const MOBILE_INACTIVITY_POLLING_MS = 1000; // 1 second.
const DESKTOP_DELAY_MS = 1000; // 1 second.

/**
 * Sets a short-lived cookie to use to prevent showing the exit modal in multiple tabs per browser session.
 *
 * Product choice: We chose to use a cookie with a short TTL for the following reasons:
 * 1. Session storage isn't shared between tabs since a new tab or window creates a new session.
 * 2. Values in session storage and session cookies can be kept around indefinitely due to how
 *    browsers implement session restores.
 *    Reference: https://developer.mozilla.org/en-US/docs/Web/HTTP/Cookies#define_the_lifetime_of_a_cookie
 * 3. Local storage doesn't have a TTL mechanism and could end up in a weird state,
 *    indefinitely blocking exit modals from showing back up,
 *    such as the 'beforeunload' event being unreliable on mobile if we were to use that to unset.
 *    Reference: https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
 */
function setExitModalShownCookie() {
    const now = new Date();
    const currentTime = now.getTime();
    now.setTime(currentTime + EXIT_MODAL_SHOW_ONCE_COOKIE_TTL);
    document.cookie = `${EXIT_MODAL_SHOW_ONCE_COOKIE_KEY}=true;expires=${now.toUTCString()};path=/`;
}

function deleteExitModalShownCookie() {
    document.cookie = `${EXIT_MODAL_SHOW_ONCE_COOKIE_KEY}=false;expires=${new Date(0).toUTCString()};path=/`;
}

function checkIfExitModalShownCookieExists() {
    return document.cookie.split(';')
        .some((cookie) => cookie.trim().startsWith(`${EXIT_MODAL_SHOW_ONCE_COOKIE_KEY}=`));
}

/**
 * Query parameters are only taken into account for Content templates.
 * If there are no query parameters to check for, either in our list or in the URL,
 * disable the exit modal by removing the element from the DOM.
 * We currently do not care about the value, only its existence.
 * The query params are case-insensitive.
 *
 * @returns {boolean} true if exit modal should be enabled, false otherwise
 */
function calculateExitModalStatusForContentTemplates() {
    if (typeof exitModalQueryParameters !== 'undefined') {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const params = Object.keys(Object.fromEntries(urlSearchParams.entries()));
        const hasMatchingQueryParam = params.some(param => exitModalQueryParameters.has(param.toLowerCase()));

        if (hasMatchingQueryParam) {
            return true;
        }
    }

    return false;
}

function setupExitModal() {
    const exitModalWrapper = document.querySelector('.exit-modal-wrapper');

    // Move the modal to be the first element in the body so the background overlay is positioned correctly.
    document.body.insertBefore(exitModalWrapper, document.body.firstChild);

    setupExitModalFocusTrap();

    document.querySelector('.exit-modal-close').addEventListener('click', () => {
        deleteExitModalShownCookie();
        sessionStorage?.setItem(EXIT_MODAL_DO_NOT_SHOW_SESSION_STORAGE_KEY, 'true');
        document.querySelector('.exit-modal-wrapper').classList.add('hide-me');
    });
}

function displayExitModal() {
    setExitModalShownCookie();

    const exitModalWrapper = document.querySelector('.exit-modal-wrapper');
    exitModalWrapper.classList.remove('hide-me');

    // Set focus to the modal for accessibility.
    exitModalWrapper.focus();

    // Manually fire a Turnstile event for the exit modal wrapper to simulate a 'view'.
    try {
        turnstile.turn(exitModalWrapper, {'data-tn-action': 'impression'});
        console.debug('Turnstile event fired for exit modal view.');
    } catch (error) {
        console.error('Failed to fire Turnstile event for exit modal view: ', error);
    }
}

/**
 * Sets up the exit modal so that tabbing through elements via keyboard navigation
 * is restricted to only elements in the modal, so that the user doesn't start tabbing
 * through other interactive elements on the page.
 */
function setupExitModalFocusTrap() {
    const exitModalWrapper = document.querySelector('.exit-modal-wrapper');
    const focusableElements = exitModalWrapper.querySelectorAll("a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type='text']:not([disabled]), input[type='radio']:not([disabled]), input[type='checkbox']:not([disabled]), select:not([disabled])");
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    exitModalWrapper.addEventListener('keydown', function(e) {
        if (e.key === 'Tab') {
            if (e.shiftKey) { // Shift + tab for going to the previous element.
                if (document.activeElement === firstFocusableElement) {
                    lastFocusableElement.focus();
                    e.preventDefault();
                }
            } else {
                if (document.activeElement === lastFocusableElement) {
                    firstFocusableElement.focus();
                    e.preventDefault();
                }
            }
        }
    });
}

window.addEventListener('DOMContentLoaded', () => {
    const exitModalWrapper = document.querySelector('.exit-modal-wrapper');
    if (!exitModalWrapper) {
        return;
    }

    if (isContentTemplate) {
        const shouldEnableExitModalForContentTemplates = calculateExitModalStatusForContentTemplates();

        if (!shouldEnableExitModalForContentTemplates) {
            console.debug('Disabling exit modal for Content template.');
            document.querySelector('.exit-modal-wrapper').remove();
            return;
        }
        console.debug('Leaving exit modal enabled for Content template based on existence of a query parameter.');
    }

    setupExitModal();

    // Define the behavior that triggers the exit modal.
    // Product choices:
    // 1. In the event that users have cookies and/or session storage disabled,
    //    we are fine with the exit modal showing up every time since the number of users affected should be small.
    // 2. Mobile (specifically when the behavior is based on inactivity) can show the modal in each tab.
    // On mobile, it displays after 10 seconds of inactivity.
    // On desktop, it displays if the user moves their mouse outside the screen, such as to close the tab.
    // Mobile device detection reference: https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_device_detection
    if (
        ('userAgentData' in navigator && navigator.userAgentData.mobile) // userAgentData isn't available in all browsers: https://developer.mozilla.org/en-US/docs/Web/API/Navigator/userAgentData#browser_compatibility
        || ('maxTouchPoints' in navigator && navigator.maxTouchPoints > 0) // Touchscreen detection.
        || ('msMaxTouchPoints' in navigator && navigator.msMaxTouchPoints > 0) // Touchscreen detection.
        || matchMedia?.('(pointer:coarse)') && matchMedia?.('(pointer:coarse)')?.media === '(pointer:coarse)' // Touchscreen detection.
        || 'orientation' in window // Deprecated touchscreen detection, but a good fallback.
        || (/Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone|IEMobile|Opera Mini/i.test(navigator.userAgent)) // According to Mozilla, user agent sniffing should be your last resort.
    ) {
        let idleTimer = 0;
        ['touchstart', 'touchmove', 'click'].forEach((eventType) => document.addEventListener(eventType, () => idleTimer = 0));

        setInterval(() => {
            idleTimer++;

            if (idleTimer >= 10 // 10 seconds of inactivity on mobile.
                && !sessionStorage?.getItem(EXIT_MODAL_DO_NOT_SHOW_SESSION_STORAGE_KEY) // Don't show the modal again in the current tab.
                && exitModalWrapper.classList.contains('hide-me')
            ) {
                displayExitModal();
            }
        }, MOBILE_INACTIVITY_POLLING_MS);
    } else {
        document.addEventListener('mouseout', (event) => {
            if (!event.toElement && !event.relatedTarget) {
                setTimeout(() => {
                    if (!checkIfExitModalShownCookieExists() // Only show the modal in one tab at a time.
                        && !sessionStorage?.getItem(EXIT_MODAL_DO_NOT_SHOW_SESSION_STORAGE_KEY) // Don't show the modal again in the current tab.
                        && exitModalWrapper.classList.contains('hide-me')
                    ) {
                        displayExitModal();
                    }
                }, DESKTOP_DELAY_MS);
            }
        });
    }
});
